import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { tap } from 'rxjs/operators'
import { Router } from '@angular/router'
import { BehaviorSubject, firstValueFrom } from 'rxjs'
import { User } from '@app/models/user.model'
import { environment } from '../../../../config/environments/environment'
import { ResponseEntity } from '@app/models/responseEntity.model'
import { omit } from 'lodash-es'
import { FormGroup } from '@angular/forms'
import { ClientContextService } from '@app/core/services/client-context.service'

const SERVER_URL = environment.serverUrl
const endpoint = SERVER_URL + '/auth'
const TOKEN_NAME = 'JWT_STR'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  requestedPasswordReset = false
  token: string | null
  private userSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null)
  user: User | null
  titleSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null)
  subTitleSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null)
  selectedPhoneState: string
  resetBody: { email: string } | { mobile: string; country_code: string; user_name: string } | null
  pinCode: string | number | null
  verifyBody: { pinCode?: string } & Record<string, unknown> = {}

  /** Current form data */
  form: FormGroup

  /** Use for check if the user click 3 times invalid pinCode and if true return it to the login page */
  countInvalidTimes: number = 0

  constructor(private http: HttpClient, private router: Router, private clientContextService: ClientContextService) {
    this.token = this.fetchTokenFromLocalStorage()
  }

  clearResetData() {
    this.resetBody = null
    this.pinCode = null
    this.requestedPasswordReset = false
  }

  /**
   * fetch token from local storage
   * @return {string} token
   */
  fetchTokenFromLocalStorage(): string | null {
    if (localStorage.getItem(TOKEN_NAME) && localStorage.getItem(TOKEN_NAME) !== 'null') {
      return localStorage.getItem(TOKEN_NAME)
    }
    return null
  }

  // get user
  async getUser(force = false): Promise<User | null> {
    if (this.user && !force) {
      return this.user
    }
    if (this.getToken() != null || force) {
      try {
        const serverData = await firstValueFrom(this.getMe())
        this.user = serverData.data ?? null
        return this.user
      } catch (_e) {
        return null
      }
    }
    return null
  }

  /**
   * Fetch current user
   */
  getMe() {
    return this.http.get<ResponseEntity<User>>(`${endpoint}/me`)
  }

  /**
   * Reset password
   * @param email
   * @param mobile
   */
  resetPassword(
    {
      email,
      mobile,
      user_name,
      country_code,
    }: {
      email?: string
      mobile?: string
      user_name?: string
      country_code?: string
    } = this.resetBody ?? {}
  ) {
    const body: Record<string, unknown> = {}
    if (email) {
      body['email'] = email
    } else if (mobile) {
      body['mobile'] = mobile
      body['country_code'] = country_code
      body['user_name'] = user_name
    }
    return this.http.post<ResponseEntity<object>>(`${endpoint}/forgot-password`, body)
  }

  validatePin(
    pinCode: number | string,
    {
      email,
      mobile,
      user_name,
      country_code,
    }: {
      email?: string
      mobile?: string
      user_name?: string
      country_code?: string
    } = this.resetBody ?? {}
  ) {
    const body: Record<string, unknown> = {
      pinCode,
    }
    if (email) {
      body['email'] = email
    } else if (mobile) {
      body['mobile'] = mobile
      body['country_code'] = country_code
      body['user_name'] = user_name
    }
    return this.http.post<ResponseEntity<object>>(`${endpoint}/validate-pin`, body)
  }

  validate2FA(pinCode: string) {
    this.verifyBody.pinCode = pinCode
    return this.http.post<ResponseEntity<object>>(`${endpoint}/validate-pin-2fa`, this.verifyBody)
  }

  applyReset(
    newPassword: string,
    {
      email,
      mobile,
      user_name,
      country_code,
    }: {
      email?: string
      mobile?: string
      user_name?: string
      country_code?: string
    } = this.resetBody ?? {}
  ) {
    const body: Record<string, unknown> = {
      pinCode: this.pinCode,
      newPassword,
    }
    if (email) {
      body['email'] = email
    } else if (mobile) {
      body['mobile'] = mobile
      body['country_code'] = country_code
      body['user_name'] = user_name
    }
    return this.http.post<ResponseEntity<object>>(`${endpoint}/reset-password`, body)
  }

  /**
   * Dropping token from service and local storage
   */
  cleanToken() {
    this.token = null
    this.setCurrentUser(null)
    localStorage.removeItem(TOKEN_NAME)
  }

  /**
   * emitting value to user subject
   * @param {User} user
   */
  setCurrentUser(user: User | null) {
    this.user = user
    this.userSubject.next(user)
  }

  /**
   * set the jwt param
   * @param {string} token
   */
  setToken(token: string) {
    this.token = token
    localStorage.setItem(TOKEN_NAME, token)
  }

  /**
   * Check if user has a permission
   * @param permission
   */
  hasPermission(permission: string) {
    return this.user?.role?.permissions?.includes(permission)
  }

  /**
   * get the jwt
   * @return {string}
   */
  getToken() {
    return this.token
  }

  /** Stable key for WHO is logged in (client id + user id claims), or null when logged out.
   *  PAPI re-signs the JWT on every GraphQL response (onSendHook injects `access_token`, which
   *  ApiService stores), so the raw token STRING rotates on virtually every API call within one
   *  session. Session-scoped caches must compare this key — comparing getToken() makes them
   *  self-invalidate after the first API round-trip. */
  getIdentityKey(): string | null {
    if (!this.token) {
      return null
    }
    try {
      const payload = this.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
      const claims = JSON.parse(atob(payload)) as { cid?: number; uid?: number }
      return `${claims.cid}:${claims.uid}`
    } catch {
      // Unparseable token: fall back to the raw string — over-invalidates but never serves
      // one identity's cache to another.
      return this.token
    }
  }

  /**
   * Login
   * @param username
   * @param password
   * @param clientId Optional client ID for organization users
   */
  login(username: string, password: string, clientId?: string) {
    const body: { username: string; password: string; client_id?: string } = { username, password }

    // Add client_id if provided (for org users)
    if (clientId) {
      body.client_id = clientId
    }

    return this.http.post<User & { access_token: string }>(`${endpoint}/login`, body).pipe(
      tap((data) => {
        this.user = omit(data, ['access_token']) as User
        this.setToken(data.access_token)
      })
    )
  }

  logout(): Promise<boolean> {
    const isOrgUser = this.clientContextService.isOrgUser()
    const clientId = this.clientContextService.getStoredClientId()

    this.cleanToken()
    this.clientContextService.clearClientId()

    // The persisted module belongs to the client that just disconnected. ActiveModuleService
    // re-validates it against the next client's modules, but events.service.getCategories reads
    // the raw localStorage value before that validation runs — drop it so the next login can't
    // query the previous client's module. (Literal key: importing it from active-module.service
    // would create a module import cycle, since that service imports AuthService.)
    localStorage.removeItem('CURRENT_MODULE')

    // Redirect to appropriate login page
    if (isOrgUser && clientId) {
      return this.router.navigate([`/org/${clientId}/auth/login`])
    } else {
      return this.router.navigate(['/auth'])
    }
  }

  /**
   * Check if organization exists and get basic info
   * @param orgSlug Organization slug/code
   * @returns Promise with organization info
   */
  async checkOrgExists(orgSlug: string): Promise<{ exists: boolean; saml_enabled?: boolean; samlEnabled?: boolean; name?: string; organizationName?: string; slug?: string }> {
    return firstValueFrom(
      this.http.get<{ exists: boolean; saml_enabled?: boolean; samlEnabled?: boolean; name?: string; organizationName?: string; slug?: string }>(
        `${endpoint}/org/check?slug=${orgSlug}`
      )
    )
  }

  /**
   * Get backend URL
   */
  getBackendUrl(): string {
    return endpoint
  }

  /**
   * Check if a client has SAML enabled
   * @param clientId Client ID or slug
   * @returns Promise with SAML status
   */
  async checkClientSAMLStatus(clientId: string): Promise<{ saml_enabled: boolean; name?: string }> {
    return firstValueFrom(this.http.get<{ saml_enabled: boolean; name?: string }>(`${endpoint}/client/${clientId}/saml-status`))
  }
}
